import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Department, DepartmentNode, OrganizationNode } from '../models/department.model';
import { User } from '../models/user.model';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DepartmentService {
  private apiUrl = environment.apiUrl;
  
  // Mock department data (as fallback)
  private mockDepartments: Department[] = [
    { id: 'd1', name: '總公司', parentId: null, level: 0 },
    { id: 'd2', name: '研發部門', parentId: 'd1', level: 1 },
    { id: 'd3', name: '行銷部門', parentId: 'd1', level: 1 },
    { id: 'd4', name: '人資部門', parentId: 'd1', level: 1 },
    { id: 'd5', name: '前端開發組', parentId: 'd2', level: 2 },
    { id: 'd6', name: '後端開發組', parentId: 'd2', level: 2 },
    { id: 'd7', name: '數位行銷組', parentId: 'd3', level: 2 },
    { id: 'd8', name: '品牌行銷組', parentId: 'd3', level: 2 },
    { id: 'd9', name: 'AI開發小組', parentId: 'd6', level: 3 },
  ];

  // Mock user data
  private mockUsers: User[] = [
    { id: 'u1', name: '王大明', email: 'wang@example.com', departmentIds: ['d1', 'd2'] },
    { id: 'u2', name: '李小英', email: 'lee@example.com', departmentIds: ['d2', 'd5'] },
    { id: 'u3', name: '張三豐', email: 'chang@example.com', departmentIds: ['d2', 'd6', 'd9'] },
    { id: 'u4', name: '陳小華', email: 'chen@example.com', departmentIds: ['d3', 'd7'] },
    { id: 'u5', name: '劉大龍', email: 'liu@example.com', departmentIds: ['d3', 'd8'] },
    { id: 'u6', name: '黃小明', email: 'huang@example.com', departmentIds: ['d4'] },
    { id: 'u7', name: '林美玲', email: 'lin@example.com', departmentIds: ['d5'] },
    { id: 'u8', name: '吳大鵬', email: 'wu@example.com', departmentIds: ['d6', 'd9'] },
    { id: 'u9', name: '鄭小婷', email: 'cheng@example.com', departmentIds: ['d7'] },
    { id: 'u10', name: '蕭大宇', email: 'hsiao@example.com', departmentIds: ['d8'] },
  ];

  // Used to track selected department
  private selectedDepartmentSubject = new BehaviorSubject<Department | null>(null);
  selectedDepartment$ = this.selectedDepartmentSubject.asObservable();

  constructor(private http: HttpClient) { }

  // Get department tree structure
  getDepartmentTree(options?: { dataScope?: string; userid?: string; assistantNo?: string }): Observable<DepartmentNode[]> {
    // First try to get real data from API
    return this.getDepartmentTreeFromAPI(options).pipe(
      catchError(error => {
        console.warn('Failed to load departments from API, using mock data:', error);
        return this.getMockDepartmentTree();
      })
    );
  }

  // Get department data from API
  private getDepartmentTreeFromAPI(options?: { dataScope?: string; userid?: string; assistantNo?: string }): Observable<DepartmentNode[]> {
    // 建立查詢參數
    let url = `${this.apiUrl}/api/departments/list`;
    const params: any = {};

    // 如果有 dataScope 參數，傳遞給後端進行權限過濾
    if (options?.dataScope && options?.userid) {
      params.dataScope = options.dataScope;
      params.userid = options.userid;
      if (options.assistantNo) {
        params.assistantNo = options.assistantNo;
      }
      console.log('◆◆ DepartmentService: 使用權限過濾參數', params);
    }

    return this.http.get<any>(url, { params }).pipe(
      map(response => {
        console.log('Department API response:', response);
        if (response && response.success && response.data) {
          return this.convertApiResponseToTree(response.data);
        }
        return [];
      })
    );
  }

  // Convert API response to DepartmentNode structure
  private convertApiResponseToTree(apiData: any[]): DepartmentNode[] {
    return apiData.map(dept => this.convertNodeToTree(dept));
  }

  // Recursively convert single node
  private convertNodeToTree(node: any): DepartmentNode {
    return {
      id: node.id,
      name: node.name,
      level: this.calculateLevel(node),
      parentId: node.parent?.id || null,
      userCount: 0, // Not provided by API, can be supplemented later
      order: 0,
      children: (node.children || []).map((child: any) => this.convertNodeToTree(child))
    };
  }

  // Calculate node level
  private calculateLevel(node: any, level: number = 0): number {
    if (!node.parent) return level;
    return level + 1;
  }

  // Tree structure using mock data (fallback)
  private getMockDepartmentTree(): Observable<DepartmentNode[]> {
    // Solution: Completely re-implement tree structure building logic

    // 1. Create Map for mapping
    const deptMap = new Map<string, DepartmentNode>();

    // 2. Generate all nodes
    this.mockDepartments.forEach(dept => {
      const userCount = this.mockUsers.filter(u => u.departmentIds.includes(dept.id)).length;

      deptMap.set(dept.id, {
        id: dept.id,
        name: dept.name,
        level: dept.level,
        parentId: dept.parentId, // Add parentId property
        userCount: userCount,
        order: dept.order || 0, // Add order property, default to 0 if not present
        children: []
      });
    });

    // 3. Build tree structure, only top-level nodes are added to the returned array
    const rootNodes: DepartmentNode[] = [];

    this.mockDepartments.forEach(dept => {
      // Get current department node
      const currentNode = deptMap.get(dept.id);
      if (!currentNode) return;

      if (dept.parentId === null) {
        // This is root node, directly add to rootNodes
        rootNodes.push(currentNode);
      } else {
        // This is child node, find its parent node and add as child
        const parentNode = deptMap.get(dept.parentId);
        if (parentNode) {
          // Important: Ensure no duplicate additions
          const alreadyAdded = parentNode.children.some(child => child.id === currentNode.id);
          if (!alreadyAdded) {
            parentNode.children.push(currentNode);
          }
        }
      }
    });

    console.log('Mock Root nodes:', JSON.stringify(rootNodes));
    return of(rootNodes);
  }

  // Flatten tree structure to department list (for dropdown menu)
  flattenDepartmentTree(tree: DepartmentNode[], options?: { enabledOnly?: boolean }): Department[] {
    const flattened: Department[] = [];

    function flatten(nodes: DepartmentNode[], level: number = 0) {
      nodes.forEach(node => {
        // Apply enabled filter if requested
        if (options?.enabledOnly && node.enabled === false) {
          console.log(`◆◆ DepartmentService.flattenDepartmentTree: Skipped disabled department - "${node.name}"`);
          return;
        }

        // Skip departments with undefined or empty names
        if (!node.name || node.name.toLowerCase() === 'undefined') {
          console.log(`◆◆ DepartmentService.flattenDepartmentTree: Skipped invalid department name - ID: ${node.id}, Name: "${node.name}"`);
          return;
        }

        flattened.push({
          id: node.id,
          name: node.name,
          parentId: node.parentId,
          level: level,
          enabled: node.enabled
        });

        if (node.children && node.children.length > 0) {
          flatten(node.children, level + 1);
        }
      });
    }

    flatten(tree);
    return flattened;
  }

  // Get flattened department list (for dropdown menu)
  getDepartmentList(options?: { enabledOnly?: boolean; dataScope?: string; userid?: string; assistantNo?: string }): Observable<Department[]> {
    // 如果有 dataScope 參數，傳遞給 getDepartmentTree
    if (options?.dataScope && options?.userid) {
      return this.getDepartmentTree({
        dataScope: options.dataScope,
        userid: options.userid,
        assistantNo: options.assistantNo
      }).pipe(
        map(tree => this.flattenDepartmentTree(tree, { enabledOnly: options.enabledOnly }))
      );
    }

    // 沒有權限過濾參數時，使用原本的邏輯
    return this.getDepartmentTree().pipe(
      map(tree => this.flattenDepartmentTree(tree, options))
    );
  }
  
  // Get valid department list (only includes enabled departments)
  getEnabledDepartmentList(): Observable<Department[]> {
    return this.getDepartmentList({ enabledOnly: true });
  }

  // ======================================================================
  // Common filtering method (used in user-management)
  // ======================================================================

  /**
   * Collect all department/company nodes from organization tree (as flat array)
   * Target nodes other than user
   * @param node Root organization node
   * @param options Filter options
   * @returns Array of department/company nodes
   */
  getAllOrganizationDepartments(node: OrganizationNode, options?: { enabledOnly?: boolean }): OrganizationNode[] {
    if (!node) {
      console.log('[DepartmentService] getAllOrganizationDepartments: node is null or undefined');
      return [];
    }

    let departments: OrganizationNode[] = [];

    // Target nodes other than user for retrieval
    if (node.type !== 'user') {
      // Apply enabled filter if requested
      const shouldInclude = !options?.enabledOnly || node.enabled !== false;
      
      if (shouldInclude) {
        // Also check name validity
        if (node.name && node.name.toLowerCase() !== 'undefined') {
          departments.push(node);
          console.log(`[DepartmentService] Found ${node.type} - ID: ${node.id}, Name: "${node.name}", Enabled: ${node.enabled !== false}`);
        } else {
          console.log(`[DepartmentService] Skipped invalid ${node.type} name - ID: ${node.id}, Name: "${node.name}"`);
        }
      } else {
        console.log(`[DepartmentService] Skipped (disabled) - ID: ${node.id}, Name: "${node.name}"`);
      }
    }

    // Recursively process children (except user)
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        if (child.type !== 'user') {
          departments = departments.concat(this.getAllOrganizationDepartments(child, options));
        }
      });
    }

    return departments;
  }

  // Get department by ID
  getDepartmentById(id: string): Department | null {
    return this.mockDepartments.find(d => d.id === id) || null;
  }

  // Get user by ID
  getUserById(id: string): User | null {
    return this.mockUsers.find(u => u.id === id) || null;
  }

  // Get users in department
  getUsersForDepartment(departmentId: string): Observable<User[]> {
    const users = this.mockUsers.filter(user =>
      user.departmentIds.includes(departmentId)
    );
    return of(users);
  }

  // Get users not in department
  getUsersNotInDepartment(departmentId: string): Observable<User[]> {
    const users = this.mockUsers.filter(user =>
      !user.departmentIds.includes(departmentId)
    );
    return of(users);
  }

  // Get user count for department
  getUserCountForDepartment(departmentId: string): number {
    return this.mockUsers.filter(user =>
      user.departmentIds.includes(departmentId)
    ).length;
  }

  // Create new department
  createDepartment(department: Omit<Department, 'id'>): Observable<Department> {
    const newId = 'd' + (this.mockDepartments.length + 1);
    const newDepartment: Department = {
      id: newId,
      name: department.name,
      parentId: department.parentId,
      level: department.level
    };

    this.mockDepartments.push(newDepartment);
    return of(newDepartment);
  }

  // Update department
  updateDepartment(department: Department): Observable<Department> {
    const index = this.mockDepartments.findIndex(d => d.id === department.id);
    if (index !== -1) {
      this.mockDepartments[index] = department;
    }
    return of(department);
  }

  // Delete department
  deleteDepartment(departmentId: string): Observable<boolean> {
    // Check if has child departments
    const hasChildren = this.mockDepartments.some(d => d.parentId === departmentId);

    if (hasChildren) {
      return of(false);  // Cannot delete if has child departments
    }

    const index = this.mockDepartments.findIndex(d => d.id === departmentId);
    if (index !== -1) {
      this.mockDepartments.splice(index, 1);

      // Remove this department ID from all users
      this.mockUsers.forEach(user => {
        user.departmentIds = user.departmentIds.filter(id => id !== departmentId);
      });
    }

    return of(true);
  }

  // Add user to department
  addUserToDepartment(userId: string, departmentId: string): Observable<boolean> {
    const user = this.mockUsers.find(u => u.id === userId);
    if (user && !user.departmentIds.includes(departmentId)) {
      user.departmentIds.push(departmentId);
    }
    return of(true);
  }

  // Remove user from department
  removeUserFromDepartment(userId: string, departmentId: string): Observable<boolean> {
    const user = this.mockUsers.find(u => u.id === userId);
    if (user) {
      user.departmentIds = user.departmentIds.filter(id => id !== departmentId);
    }
    return of(true);
  }

  // Select department
  selectDepartment(department: Department): void {
    this.selectedDepartmentSubject.next(department);
  }

  /**
   * Update department order within parent
   * 
   * Note: Uses individual department update API if /reorder endpoint is not available
   */
  updateDepartmentOrder(assistantNo: string, parentId: string, childIds: string[]): Observable<any> {
    // Try the dedicated reorder endpoint first
    const reorderUrl = `${this.apiUrl}/api/departments/${assistantNo}/reorder`;
    const reorderData = {
      parentId: parentId,
      childIds: childIds
    };
    
    console.log('[Department Order] Updating order:', { parentId, childCount: childIds.length });
    
    return this.http.put<any>(reorderUrl, reorderData).pipe(
      catchError(error => {
        console.warn('[Department Order] Reorder endpoint not available (status:', error.status, ')');
        
        // Fallback: Use individual department update API
        if (error.status === 404 || error.status === 0) {
          return this.updateDepartmentOrderFallback(assistantNo, childIds);
        }
        
        // For other errors, return failure
        return of({ success: false, error: error.message });
      })
    );
  }

  /**
   * Fallback method: Update order using individual department updates
   * 
   * Note: Backend returns entire organization tree after each update,
   * so we only need to update the last department to trigger full tree reload
   */
  private updateDepartmentOrderFallback(assistantNo: string, childIds: string[]): Observable<any> {
    console.error('[Department Order] Fallback failed: Backend does not support order field updates');
    console.error('[Department Order] Please implement PUT /api/departments/:assistantNo/reorder endpoint');
    
    return of({ 
      success: false, 
      method: 'fallback',
      error: 'Backend does not support order field updates. Please implement /reorder endpoint.'
    });
  }
}
